import { useState } from 'react';
import { Segmented, Table, Tooltip, Empty, theme } from 'antd';
import { ThunderboltOutlined, NumberOutlined, CheckCircleOutlined, DollarOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { getAiUsage, type UsageByModel } from '@/api/aiUsage';
import { KpiTile } from '@/components/KpiTile';

const KIND_LABEL: Record<string, string> = { translate: 'Translate', extract: 'Extract' };

export function UsageSection() {
  const { token } = theme.useToken();
  const [days, setDays] = useState<number>(30);
  const { data, isLoading } = useQuery({ queryKey: ['ai-usage', days], queryFn: () => getAiUsage(days) });

  const t = data?.totals;
  const trend = data?.trend ?? [];
  const byKind = data?.by_kind ?? [];
  const maxTok = Math.max(1, ...trend.map((p) => p.tokens));
  const kindTotal = Math.max(1, byKind.reduce((s, k) => s + k.total_tokens, 0));

  return (
    <div>
      <div style={{ textAlign: 'right', marginBottom: 12 }}>
        <Segmented
          value={days}
          onChange={(v) => setDays(v as number)}
          options={[{ label: '7d', value: 7 }, { label: '30d', value: 30 }, { label: '90d', value: 90 }]}
        />
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <KpiTile tone="sky" icon={<ThunderboltOutlined />} label="Calls" value={t?.calls ?? 0} />
        <KpiTile tone="indigo" icon={<NumberOutlined />} label="Tokens" value={(t?.total_tokens ?? 0).toLocaleString()} />
        <KpiTile tone="green" icon={<CheckCircleOutlined />} label="Success" value={`${Math.round((t?.success_rate ?? 0) * 100)}%`} />
        <KpiTile tone="amber" icon={<DollarOutlined />} label="Cost" value={`$${(t?.cost_usd ?? 0).toFixed(4)}`} />
      </div>

      {trend.length > 0 ? (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: token.colorTextSecondary, marginBottom: 6 }}>
            <span>Tokens / day · last {days}d</span>
            <span>peak {maxTok.toLocaleString()} tok</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 80 }}>
            {trend.map((p) => (
              <Tooltip key={p.day} title={`${p.day}: ${p.tokens.toLocaleString()} tok · $${p.cost_usd.toFixed(4)}`}>
                <div
                  style={{
                    flex: 1, minWidth: 4,
                    height: `${Math.max(4, Math.round((p.tokens / maxTok) * 100))}%`,
                    background: 'linear-gradient(180deg,#6366f1,#7c3aed)', borderRadius: 3,
                  }}
                />
              </Tooltip>
            ))}
          </div>
        </div>
      ) : (
        !isLoading && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No usage yet — run a translate/extract job with a configured model."
            style={{ margin: '12px 0 20px' }}
          />
        )
      )}

      <Table<UsageByModel>
        rowKey="model"
        size="small"
        loading={isLoading}
        dataSource={data?.by_model ?? []}
        pagination={false}
        columns={[
          { title: 'Model', dataIndex: 'model' },
          { title: 'Provider', dataIndex: 'provider' },
          { title: 'Calls', dataIndex: 'calls' },
          { title: 'Tokens', dataIndex: 'total_tokens', render: (v: number) => v.toLocaleString() },
          { title: 'Cost', dataIndex: 'cost_usd', render: (v: number) => `$${v.toFixed(4)}` },
        ]}
      />

      {byKind.length > 0 && (
        <div style={{ marginTop: 16 }}>
          {byKind.map((k) => (
            <div key={k.kind} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span>{KIND_LABEL[k.kind] ?? k.kind}</span>
                <span style={{ color: token.colorTextSecondary }}>
                  {k.calls} calls · {k.total_tokens.toLocaleString()} tok · ${k.cost_usd.toFixed(4)}
                </span>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: token.colorFillSecondary }}>
                <div style={{ height: 8, borderRadius: 4, width: `${Math.round((k.total_tokens / kindTotal) * 100)}%`, background: '#0ea5e9' }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
